import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Icon,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { HealthAndSafety, Close } from '@mui/icons-material';
import { Fragment } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMenuForRole } from './menuConfig';

export const DRAWER_WIDTH = 260;

interface SidebarProps {
  /** Offset superior (px) cuando hay banner de impersonacion */
  topOffset?: number;
  /** Estado del drawer en mobile (open/closed) */
  mobileOpen: boolean;
  /** Handler para cerrar el drawer en mobile */
  onCloseMobile: () => void;
}

export default function Sidebar({
  topOffset = 0,
  mobileOpen,
  onCloseMobile,
}: SidebarProps) {
  const { profile, effectiveRole } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!profile || !effectiveRole) return null;

  // Usar el rol efectivo: cuando hay impersonacion, el sidebar muestra el
  // menu de admin_empresa (no el del admin real).
  const menu = getMenuForRole(effectiveRole);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleItemClick = () => {
    // En mobile, cerrar el drawer al hacer click en un item
    if (isMobile) onCloseMobile();
  };

  const drawerContent = (
    <>
      {/* Branding (con boton cerrar en mobile) */}
      <Box
        sx={{
          p: 2,
          textAlign: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'relative',
        }}
      >
        {isMobile && (
          <IconButton
            onClick={onCloseMobile}
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8 }}
            aria-label="Cerrar menu"
          >
            <Close />
          </IconButton>
        )}
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            width: 48,
            height: 48,
            mx: 'auto',
            mb: 1,
          }}
        >
          <HealthAndSafety sx={{ fontSize: 28 }} />
        </Avatar>
        <Typography variant="subtitle1" fontWeight={700} color="primary">
          ATSA Rio Negro
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Sistema de Gestion v2.0
        </Typography>
      </Box>

      <List sx={{ pt: 1, overflowY: 'auto', flex: 1 }}>
        {menu.map((group, gi) => (
          <Fragment key={group.group}>
            {gi > 0 && <Divider sx={{ my: 0.5, mx: 2 }} />}
            <Typography
              variant="overline"
              sx={{
                px: 2,
                py: 0.5,
                display: 'block',
                color: 'text.secondary',
                fontSize: 11,
                letterSpacing: 1,
              }}
            >
              {group.group}
            </Typography>
            {group.items.map((item) => {
              const active = isActive(item.path);
              return (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    selected={active}
                    onClick={handleItemClick}
                    sx={{
                      mx: 1,
                      borderRadius: 2,
                      mb: 0.5,
                      '&.Mui-selected': {
                        bgcolor: 'primary.light',
                        color: 'primary.main',
                        '& .MuiListItemIcon-root': { color: 'primary.main' },
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: 'primary.light',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Icon>{item.icon}</Icon>
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: active ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </Fragment>
        ))}
      </List>
    </>
  );

  if (isMobile) {
    // Drawer temporal (overlay) en mobile
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onCloseMobile}
        ModalProps={{ keepMounted: true }} // mejor performance en mobile
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Drawer permanente en desktop
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: 'none',
          boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
          mt: `${topOffset}px`,
          height: `calc(100vh - ${topOffset}px)`,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
